import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { publicApi, formatApiError } from "@/lib/api";

export const EscalationForm = ({ product, sessionId, onDone }) => {
  const [form, setForm] = useState({
    product: product?.name || "",
    fault_code: "",
    summary: "",
    attempted: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
  });
  const [stage, setStage] = useState("editing");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const field = (key, label, options = {}) => (
    <label className="block">
      <span className="text-xs font-display font-bold text-ei-navy uppercase tracking-wider">{label}</span>
      {options.textarea ? (
        <textarea
          rows={options.rows || 3}
          value={form[key]}
          onChange={(event) => setForm({ ...form, [key]: event.target.value })}
          className="mt-1 w-full border border-ei-mist rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-ei-navy"
          data-testid={`escalate-${key}`}
        />
      ) : (
        <input
          type={options.type || "text"}
          value={form[key]}
          onChange={(event) => setForm({ ...form, [key]: event.target.value })}
          className="mt-1 w-full border border-ei-mist rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-ei-navy min-h-[40px]"
          data-testid={`escalate-${key}`}
        />
      )}
      {options.hint && <span className="text-[11px] text-ei-graphite/55">{options.hint}</span>}
    </label>
  );

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await publicApi.post("/support/escalate", {
        session_id: sessionId || null,
        product: form.product.trim(),
        fault_code: form.fault_code.trim() || null,
        summary: form.summary.trim(),
        attempted: form.attempted.trim() || null,
        contact_name: form.contact_name.trim() || null,
        contact_email: form.contact_email.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
      });
      setResult(data);
      setStage("sent");
    } catch (error) {
      toast.error(formatApiError(error, "Your request could not be submitted"));
    } finally {
      setBusy(false);
    }
  };

  if (stage === "sent" && result) {
    const delivered = result.email_delivered;
    return (
      <div className="p-5 bg-white border border-ei-mist rounded-md" data-testid="escalate-result">
        <div className="flex items-start gap-3">
          {delivered ? <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />}
          <div>
            <h3 className="font-display text-base font-bold text-ei-navy">Request {result.reference} saved</h3>
            <p className="text-sm text-ei-graphite mt-1">
              {delivered
                ? `Your request has been emailed to our technical team at ${result.support_email}. Quote ${result.reference} if you follow up.`
                : `Your request is recorded in our support desk and our team will see it, but the email to ${result.support_email} did not go out. If it is urgent, email ${result.support_email} directly and quote ${result.reference}.`}
            </p>
            <button onClick={onDone} className="mt-3 text-xs font-display font-bold text-ei-navy hover:text-ei-gold" data-testid="escalate-close">
              Back to the assistant
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (stage === "review") {
    return (
      <div className="p-5 bg-white border border-ei-mist rounded-md space-y-3" data-testid="escalate-review">
        <h3 className="font-display text-base font-bold text-ei-navy">Check your request before sending</h3>
        <dl className="text-sm space-y-2">
          {[
            ["Product", form.product],
            ["Fault code", form.fault_code || "Not supplied"],
            ["Issue", form.summary],
            ["Already tried", form.attempted || "Not supplied"],
            ["Name", form.contact_name || "Not supplied"],
            ["Email", form.contact_email || "Not supplied"],
            ["Phone", form.contact_phone || "Not supplied"],
          ].map(([label, value]) => (
            <div key={label} className="flex gap-3">
              <dt className="w-28 shrink-0 text-ei-graphite/60 text-xs uppercase tracking-wider pt-0.5">{label}</dt>
              <dd className="text-ei-navy whitespace-pre-wrap">{value}</dd>
            </div>
          ))}
        </dl>
        <div className="flex flex-wrap gap-2 pt-2">
          <button onClick={submit} disabled={busy} className="inline-flex items-center gap-2 bg-ei-navy text-ei-white font-display text-xs font-bold px-4 py-3 rounded-sm hover:bg-ei-navy/90 disabled:opacity-40 min-h-[44px]" data-testid="escalate-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to technical support
          </button>
          <button onClick={() => setStage("editing")} className="font-display text-xs font-bold text-ei-navy border border-ei-mist px-4 py-3 rounded-sm hover:bg-ei-navy/5 min-h-[44px]" data-testid="escalate-edit">
            Keep editing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 bg-white border border-ei-mist rounded-md space-y-4" data-testid="escalate-form">
      <div>
        <h3 className="font-display text-base font-bold text-ei-navy">Send this to a human specialist</h3>
        <p className="text-sm text-ei-graphite/70 mt-1">You will see the full request before anything is sent.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {field("product", "Product")}
        {field("fault_code", "Fault code shown on the display", { hint: "Exactly as displayed, if there is one" })}
      </div>
      {field("summary", "What is happening", { textarea: true, rows: 4 })}
      {field("attempted", "What has already been tried", { textarea: true, rows: 3 })}
      <div className="grid sm:grid-cols-3 gap-4">
        {field("contact_name", "Your name (optional)")}
        {field("contact_email", "Email (optional)", { type: "email" })}
        {field("contact_phone", "Phone (optional)")}
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setStage("review")}
          disabled={form.product.trim().length < 2 || form.summary.trim().length < 10}
          className="inline-flex items-center gap-2 bg-ei-navy text-ei-white font-display text-xs font-bold px-4 py-3 rounded-sm hover:bg-ei-navy/90 disabled:opacity-40 min-h-[44px]"
          data-testid="escalate-review-button"
        >
          Review request
        </button>
        <button onClick={onDone} className="font-display text-xs font-bold text-ei-navy border border-ei-mist px-4 py-3 rounded-sm hover:bg-ei-navy/5 min-h-[44px]" data-testid="escalate-cancel">
          Cancel
        </button>
      </div>
    </div>
  );
};

export default EscalationForm;
